package com.elan.edubridge.ui.staff

import com.elan.edubridge.shared.di.SharedGraph
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.JoinApplication
import com.elan.edubridge.shared.domain.model.JoinApplicationStatus
import com.elan.edubridge.shared.domain.model.Permission
import com.elan.edubridge.shared.domain.model.ReadResult
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.shared.domain.model.StudentData
import com.elan.edubridge.ui.shell.Router
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.formatTimestamp
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.shell.partialFailureHtml
import com.elan.edubridge.ui.shell.readErrorHtml
import com.elan.edubridge.ui.shell.wireRetry
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.HTMLSelectElement

/**
 * W7 가입 신청 큐(EDITOR 이상). [ContentReportsPage]와 같은 자리의 스태프 대기열이다.
 *
 * 신청자 표시는 이름·이메일뿐이다 — 동의 체크박스가 전달을 약속한 최소 PII가 그 둘이고,
 * 문서 id가 uid라는 사실은 화면에 올리지 않는다. 승인 모달은 "새로 등록 / 기존 학생에 연결"을
 * 명시 선택으로 받는다(설계서 §8③) — 기존 학생 피커는 계정이 아직 연결되지 않은
 * (`userId == null`) 학생만 보여 준다. 이미 연결된 학생은 서버가 어차피 거부한다.
 */
object JoinApplicationsPage {
    private val scope = MainScope()

    /** 읽기 실패·부분 실패 양쪽의 "다시 시도" 버튼 id(#30). */
    private const val RETRY_ID = "join-applications-retry"
    private val repository = SharedGraph.createRepository()
    private val commandRepository = SharedGraph.createAcademyCommandRepository()

    fun open(session: SessionState, academyId: String) {
        openFiltered(session, academyId, JoinApplicationStatus.PENDING)
    }

    private fun openFiltered(session: SessionState, academyId: String, filter: JoinApplicationStatus) {
        if (!session.activePermission.atLeast(Permission.EDITOR)) {
            Router.replace("/app/$academyId/today")
            return
        }
        scope.launch {
            render(session, academyId, filter, repository.joinApplicationsResult(academyId, filter))
        }
    }

    private fun render(
        session: SessionState,
        academyId: String,
        filter: JoinApplicationStatus,
        result: ReadResult<JoinApplication>,
    ) {
        DashboardShell.render(session, academyId, "join-applications", pageHtml(filter, result)) {
            wireFilterTabs(session, academyId, filter)
            wireRetry(RETRY_ID) { openFiltered(session, academyId, filter) }
            wireRows(session, academyId, filter, result.items)
        }
    }

    /**
     * 대기 큐가 비어 보이는 것과 못 읽는 것은 다르다(#30) — 후자는 "기다리는 신청자가 있는지
     * 모른다"이고, 신청자는 그동안 승인을 기다린다.
     */
    private fun pageHtml(filter: JoinApplicationStatus, result: ReadResult<JoinApplication>): String {
        val applications = result.items
        val body = when {
            result.isEmptySuccess -> emptyHtml(filter)
            applications.isEmpty() -> readErrorHtml(result.failure ?: CommandFailure.INTERNAL, RETRY_ID)
            else -> applications.mapIndexed { index, application -> rowHtml(index, filter, application) }.joinToString("")
        }
        val partialBanner = if (result.isPartialFailure) partialFailureHtml(RETRY_ID) else ""
        return """
            <div class="eb-page-header">
                <div>
                    <h1 class="eb-page-title">가입 신청</h1>
                    <p class="eb-meta">참여 코드로 들어온 학생 가입 신청을 검토해 주세요. 승인하면 학생 멤버십이 만들어져요.</p>
                </div>
            </div>
            <div class="eb-filter-chips" role="tablist" aria-label="가입 신청 필터">
                ${filterTabHtml(JoinApplicationStatus.PENDING, "대기", filter)}
                ${filterTabHtml(JoinApplicationStatus.BLOCKED, "차단됨", filter)}
            </div>
            $partialBanner
            <section class="eb-card" aria-label="가입 신청 목록">$body</section>
        """.trimIndent()
    }

    private fun filterTabHtml(status: JoinApplicationStatus, label: String, active: JoinApplicationStatus): String {
        val activeClass = if (status == active) " eb-filter-chip--active" else ""
        return """<button class="eb-filter-chip$activeClass join-application-filter" type="button" role="tab" aria-selected="${status == active}" data-filter="${status.name}">$label</button>"""
    }

    private fun emptyHtml(filter: JoinApplicationStatus): String = when (filter) {
        JoinApplicationStatus.BLOCKED -> """
            <div class="eb-empty-state">${icon("icon-check", "xl")}<h2>차단된 신청이 없어요</h2><p>반려할 때 "다시 신청 차단"을 선택하면 이곳에서 관리할 수 있어요.</p></div>
        """.trimIndent()
        else -> """
            <div class="eb-empty-state">${icon("icon-check", "xl")}<h2>대기 중인 신청이 없어요</h2><p>설정에서 참여 코드를 발급해 학원 안내문에 붙여 보세요.</p></div>
        """.trimIndent()
    }

    private fun rowHtml(index: Int, filter: JoinApplicationStatus, application: JoinApplication): String {
        val name = (application.applicantName ?: "이름 미등록").escapeHtml()
        val email = application.applicantEmail?.escapeHtml() ?: "이메일 없음"
        val retry = if (application.attemptCount > 1) " · 재신청 ${application.attemptCount}회차" else ""
        val actions = when (filter) {
            JoinApplicationStatus.BLOCKED -> """
                <button class="eb-button eb-button--ghost eb-button--sm join-application-unblock" type="button" data-application-index="$index">차단 해제</button>
            """.trimIndent()
            else -> """
                <button class="eb-button eb-button--ghost eb-button--sm join-application-reject" type="button" data-application-index="$index">반려</button>
                <button class="eb-button eb-button--primary eb-button--sm join-application-approve" type="button" data-application-index="$index">승인</button>
            """.trimIndent()
        }
        return """
            <article class="eb-notice-item">
                ${icon("icon-students", "md")}
                <div>
                    <strong>$name</strong>
                    <span class="eb-meta">$email · ${formatTimestamp(application.submittedAt)} 신청$retry</span>
                </div>
                <div class="eb-notice-actions">$actions</div>
            </article>
        """.trimIndent()
    }

    private fun wireFilterTabs(session: SessionState, academyId: String, current: JoinApplicationStatus) {
        val tabs = document.querySelectorAll(".join-application-filter")
        for (index in 0 until tabs.length) {
            val tab = tabs.item(index) as? HTMLElement ?: continue
            tab.onclick = {
                val next = JoinApplicationStatus.entries.firstOrNull { it.name == tab.getAttribute("data-filter") }
                if (next != null && next != current) openFiltered(session, academyId, next)
                null
            }
        }
    }

    private fun wireRows(
        session: SessionState,
        academyId: String,
        filter: JoinApplicationStatus,
        applications: List<JoinApplication>,
    ) {
        fun wire(selector: String, onPick: (JoinApplication) -> Unit) {
            val buttons = document.querySelectorAll(selector)
            for (index in 0 until buttons.length) {
                val button = buttons.item(index) as? HTMLElement ?: continue
                button.onclick = {
                    applications.getOrNull(button.getAttribute("data-application-index")?.toIntOrNull() ?: -1)
                        ?.let(onPick)
                    null
                }
            }
        }
        wire(".join-application-approve") { openApprovalEditor(session, academyId, it) }
        wire(".join-application-reject") { openRejectEditor(session, academyId, it) }
        wire(".join-application-unblock") { application ->
            scope.launch {
                val result = commandRepository.unblockAcademyJoinApplication(academyId, application.applicantUid)
                if (result.isSuccess) {
                    Toast.success("차단을 해제했어요. 신청자는 24시간 뒤부터 다시 신청할 수 있어요.")
                } else {
                    Toast.error(result.errorMessage ?: "차단을 해제하지 못했어요. 다시 시도해 주세요.")
                }
                openFiltered(session, academyId, filter)
            }
        }
    }

    /**
     * 승인 모달. 학생 피커는 모달을 열 때 로드한다 — 큐 목록 화면이 학생 전체 조회를
     * 매번 끌고 다닐 이유가 없다.
     */
    private fun openApprovalEditor(session: SessionState, academyId: String, application: JoinApplication) {
        val name = (application.applicantName ?: "이름 미등록").escapeHtml()
        val backdrop = document.createElement("div") as HTMLElement
        backdrop.className = "eb-modal-backdrop"
        backdrop.innerHTML = """
            <div class="eb-modal" role="dialog" aria-modal="true" aria-labelledby="join-approve-title">
                <header class="eb-modal__header">
                    <h2 id="join-approve-title">가입 승인 · $name</h2>
                    <button class="eb-icon-button" id="join-approve-close" type="button" aria-label="닫기">${icon("icon-close", "md")}</button>
                </header>
                <form class="eb-modal__body" id="join-approve-form">
                    <div class="eb-field">
                        <label><input type="radio" name="join-approve-mode" value="create" checked> 새 학생으로 등록</label>
                        <p class="eb-meta">신청자 이름으로 학생 명단에 새 학생을 만들어 연결해요.</p>
                    </div>
                    <div class="eb-field">
                        <label><input type="radio" name="join-approve-mode" value="link"> 기존 학생에 연결</label>
                        <p class="eb-meta">명단에 미리 등록해 둔 학생과 이 계정을 연결해요. 출결 이력이 그대로 유지돼요.</p>
                        <select id="join-approve-student" disabled aria-label="연결할 학생 선택">
                            <option value="">학생 목록을 불러오는 중이에요…</option>
                        </select>
                    </div>
                    <p class="eb-form-error" id="join-approve-error" role="alert" hidden></p>
                </form>
                <footer class="eb-modal__footer">
                    <button class="eb-button eb-button--ghost" id="join-approve-cancel" type="button">취소</button>
                    <button class="eb-button eb-button--primary" id="join-approve-submit" type="submit" form="join-approve-form">승인</button>
                </footer>
            </div>
        """.trimIndent()
        document.body?.appendChild(backdrop)

        var submitting = false
        var linkable: List<StudentData> = emptyList()
        fun close() {
            if (!submitting) backdrop.remove()
        }
        fun setSubmitting(value: Boolean) {
            submitting = value
            listOf("#join-approve-close", "#join-approve-cancel", "#join-approve-submit").forEach { selector ->
                (backdrop.querySelector(selector) as? HTMLButtonElement)?.disabled = value
            }
        }
        fun showError(message: String) {
            (backdrop.querySelector("#join-approve-error") as? HTMLElement)?.let { error ->
                error.textContent = message
                error.removeAttribute("hidden")
            }
        }
        fun selectedMode(): String =
            (backdrop.querySelector("input[name=join-approve-mode]:checked") as? HTMLInputElement)?.value ?: "create"
        fun studentSelect(): HTMLSelectElement? = backdrop.querySelector("#join-approve-student") as? HTMLSelectElement

        (backdrop.querySelector("#join-approve-close") as? HTMLElement)?.onclick = { close(); null }
        (backdrop.querySelector("#join-approve-cancel") as? HTMLElement)?.onclick = { close(); null }
        backdrop.onclick = { event -> if (event.target == backdrop) close(); null }

        // 라디오와 피커 활성화를 묶는다 — "기존 학생에 연결"을 고르기 전에는 피커가 비활성이다.
        val radios = backdrop.querySelectorAll("input[name=join-approve-mode]")
        for (index in 0 until radios.length) {
            (radios.item(index) as? HTMLInputElement)?.onchange = {
                studentSelect()?.disabled = selectedMode() != "link" || linkable.isEmpty()
                null
            }
        }

        scope.launch {
            // 명단을 못 읽은 것을 "전원 연결됨"으로 그리면 스태프가 이미 있는 학생을 또 만든다(#30).
            val roster = repository.studentsResult(academyId)
            linkable = roster.items.filter { it.userId == null }
            studentSelect()?.let { select ->
                if (linkable.isEmpty()) {
                    select.innerHTML = if (roster.isSuccess) {
                        """<option value="">연결할 수 있는 학생이 없어요 (전원 연결됨)</option>"""
                    } else {
                        """<option value="">학생 명단을 불러오지 못했어요 — "새 학생으로 등록"을 쓰거나 잠시 후 다시 열어 주세요</option>"""
                    }
                    select.disabled = true
                } else {
                    select.innerHTML = linkable.joinToString(
                        separator = "",
                        prefix = """<option value="">연결할 학생을 선택해 주세요</option>""",
                    ) {
                        val label = if (it.grade.isBlank()) it.name else "${it.name} · ${it.grade}"
                        """<option value="${it.id.escapeHtml()}">${label.escapeHtml()}</option>"""
                    }
                    select.disabled = selectedMode() != "link"
                }
            }
        }

        (backdrop.querySelector("#join-approve-form") as? HTMLFormElement)?.onsubmit = { event ->
            event.preventDefault()
            if (!submitting) {
                val studentId = if (selectedMode() == "link") studentSelect()?.value?.takeIf { it.isNotBlank() } else null
                if (selectedMode() == "link" && studentId == null) {
                    showError("연결할 학생을 선택하거나 \"새 학생으로 등록\"을 선택해 주세요.")
                } else {
                    setSubmitting(true)
                    scope.launch {
                        val result = commandRepository.approveAcademyJoinApplication(
                            academyId,
                            application.applicantUid,
                            studentId,
                        )
                        if (result.isSuccess) {
                            backdrop.remove()
                            Toast.success("가입을 승인했어요.")
                            openFiltered(session, academyId, JoinApplicationStatus.PENDING)
                        } else {
                            showError(result.errorMessage ?: "가입을 승인하지 못했어요. 다시 시도해 주세요.")
                            setSubmitting(false)
                        }
                    }
                }
            }
            null
        }
    }

    /** 반려 모달. 차단은 반려의 강한 형태라 같은 창에서 체크박스 하나로 고른다(`blockFuture`). */
    private fun openRejectEditor(session: SessionState, academyId: String, application: JoinApplication) {
        val name = (application.applicantName ?: "이름 미등록").escapeHtml()
        val backdrop = document.createElement("div") as HTMLElement
        backdrop.className = "eb-modal-backdrop"
        backdrop.innerHTML = """
            <div class="eb-modal" role="dialog" aria-modal="true" aria-labelledby="join-reject-title">
                <header class="eb-modal__header">
                    <h2 id="join-reject-title">가입 반려 · $name</h2>
                    <button class="eb-icon-button" id="join-reject-close" type="button" aria-label="닫기">${icon("icon-close", "md")}</button>
                </header>
                <form class="eb-modal__body" id="join-reject-form">
                    <p>반려하면 신청자는 24시간 뒤부터 같은 코드로 다시 신청할 수 있어요.</p>
                    <div class="eb-field">
                        <label><input type="checkbox" id="join-reject-block"> 다시 신청 차단</label>
                        <p class="eb-meta">이 계정의 재신청을 막아요. "차단됨" 목록에서 언제든 해제할 수 있어요.</p>
                    </div>
                    <p class="eb-form-error" id="join-reject-error" role="alert" hidden></p>
                </form>
                <footer class="eb-modal__footer">
                    <button class="eb-button eb-button--ghost" id="join-reject-cancel" type="button">취소</button>
                    <button class="eb-button eb-button--primary" id="join-reject-submit" type="submit" form="join-reject-form">반려</button>
                </footer>
            </div>
        """.trimIndent()
        document.body?.appendChild(backdrop)

        var submitting = false
        fun close() {
            if (!submitting) backdrop.remove()
        }
        fun showError(message: String) {
            (backdrop.querySelector("#join-reject-error") as? HTMLElement)?.let { error ->
                error.textContent = message
                error.removeAttribute("hidden")
            }
        }

        (backdrop.querySelector("#join-reject-close") as? HTMLElement)?.onclick = { close(); null }
        (backdrop.querySelector("#join-reject-cancel") as? HTMLElement)?.onclick = { close(); null }
        backdrop.onclick = { event -> if (event.target == backdrop) close(); null }
        (backdrop.querySelector("#join-reject-form") as? HTMLFormElement)?.onsubmit = { event ->
            event.preventDefault()
            if (!submitting) {
                submitting = true
                listOf("#join-reject-close", "#join-reject-cancel", "#join-reject-submit").forEach { selector ->
                    (backdrop.querySelector(selector) as? HTMLButtonElement)?.disabled = true
                }
                val blockFuture = (backdrop.querySelector("#join-reject-block") as? HTMLInputElement)?.checked == true
                scope.launch {
                    val result = commandRepository.rejectAcademyJoinApplication(
                        academyId,
                        application.applicantUid,
                        blockFuture,
                    )
                    if (result.isSuccess) {
                        backdrop.remove()
                        Toast.success(if (blockFuture) "신청을 반려하고 차단했어요." else "신청을 반려했어요.")
                        openFiltered(session, academyId, JoinApplicationStatus.PENDING)
                    } else {
                        showError(result.errorMessage ?: "신청을 반려하지 못했어요. 다시 시도해 주세요.")
                        submitting = false
                        listOf("#join-reject-close", "#join-reject-cancel", "#join-reject-submit").forEach { selector ->
                            (backdrop.querySelector(selector) as? HTMLButtonElement)?.disabled = false
                        }
                    }
                }
            }
            null
        }
    }

}
